import { MobileNavigationTabBar } from '@src/containers/Mobile/MobileNavigationTabBar';
import { buildOrderDetailsRoutes } from '@src/containers/Trading/Markets/OrderDetails/routingUtils/buildOrderDetailsRoutes';
import { useAppLayoutForRouteParams, useCanonicalLayoutIdForSection } from '@src/hooks/useAppLayoutId';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRedirectSpacesRoute } from '@src/hooks/useSpaceSectionRedirectLocation';
import { useSpacesRouting } from '@src/hooks/useSpacesRouting';
import { useAppLayoutConfig } from '@src/providers/AppLayoutConfig/AppLayoutConfigContext';
import { MobileLayoutPanelProvider } from '@src/providers/MobileLayoutPanelProvider/MobileLayoutPanelProvider';
import { TRADING_ANALYTICS_BASE_PATH, type NavigationItem } from '@src/tokens/navigationPaths';
import type { LayoutSpec } from '@src/types/LayoutConfig';
import { BottomScrollGradient, LoaderTalos, TopScrollGradient } from '@talos/kyoko';
import { useEffect } from 'react';
import { matchRoutes, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { NoMatchPage } from '../../NoMatchPage';
import { AnalyticsCanonicalRedirect } from '../../Routes/AnalyticsLegacyRedirect';
import { MobileAppHeader } from '../MobileAppHeader/MobileAppHeader';
import { MobileUnsupportedSectionPlaceholder } from '../MobileUnsupportedSectionPlaceholder';
import { getTradingPanelRoute } from './Blotters/navRoutes';
import { MobileOrderDetailsRoute } from './MobileOrderDetailsRoute';
import {
  getMobileFriendlyPanelForActiveLayoutPanel,
  MobileTradingPanelDispatcher,
} from './MobileTradingPanelDispatcher';
import { MobileSymbolDeepDiveRoute } from './SymbolDeepDive';
import {
  NO_MOBILE_FRIENDLY_PANELS_MESSAGE,
  NO_MOBILE_FRIENDLY_PANELS_TITLE,
  TradingPlaceholder,
} from './TradingPlaceholder';
import { useMobileTradingNavItems } from './useMobileTradingNavItems';
import { getRedirectToDefaultChildPath } from './utils/getRedirectToDefaultChildPath';

const LAYOUT_SECTION = 'trading' as const;

/**
 * Top-level route for `/trading/*` on mobile. Matches both URL shapes — `/trading/:layoutId/*` and
 * `/trading/:navGroupName/:layoutId/*` — and redirects bare `/trading` to the user's canonical space path.
 * Mirrors the desktop `TradingRoute` shell so both platforms share URL semantics.
 */
export function MobileTradingRoute() {
  const canonicalLayoutId = useCanonicalLayoutIdForSection(LAYOUT_SECTION);
  const { enableAnalyticsInTrading, isLoaded: areFeatureFlagsLoaded } = useFeatureFlag();
  const { navGroupNames, indexRedirectPath } = useSpacesRouting(LAYOUT_SECTION, canonicalLayoutId);

  return (
    <Routes>
      <Route index element={<Navigate to={indexRedirectPath} replace />} />

      <Route
        path={`${TRADING_ANALYTICS_BASE_PATH.slice('/trading/'.length)}/*`}
        element={
          !areFeatureFlagsLoaded ? (
            <LoaderTalos />
          ) : enableAnalyticsInTrading ? (
            <MobileUnsupportedSectionPlaceholder label="Analytics" />
          ) : (
            <AnalyticsCanonicalRedirect />
          )
        }
      />

      {navGroupNames.map(navGroupName => (
        <Route
          key={navGroupName}
          path={`${navGroupName}/:layoutId/*`}
          element={<MobileTradingLayout navGroupName={navGroupName} />}
        />
      ))}

      <Route path=":layoutId/*" element={<MobileTradingLayout />} />

      <Route path="*" element={<NoMatchPage />} />
    </Routes>
  );
}

interface MobileTradingLayoutProps {
  /** Set when matched under `/trading/:navGroupName/:layoutId/*`; omitted for `/trading/:layoutId/*`. */
  navGroupName?: string;
}

/**
 * Mobile trading layout shell for both URL shapes: `/trading/:layoutId/*` and `/trading/:navGroupName/:layoutId/*`.
 * Mirrors desktop `TradingSpaceAppLayout`: delegates unknown layout ids and stale/missing group slugs to
 * {@link useRedirectSpacesRoute} instead of silently substituting a layout.
 */
function MobileTradingLayout({ navGroupName }: MobileTradingLayoutProps) {
  const { layoutId } = useParams<{ layoutId?: string }>();
  const { layoutConfigsMap, selectedLayoutId, updateSelectedLayoutId } = useAppLayoutConfig(LAYOUT_SECTION);
  const spaceRedirect = useRedirectSpacesRoute(LAYOUT_SECTION, navGroupName);
  const tradingNavItems = useMobileTradingNavItems();
  const location = useLocation();
  const showMobileNavMenu = matchRoutes(tradingNavItems, location)?.at(0) != null;
  const layout = layoutId != null ? layoutConfigsMap?.[layoutId] : undefined;

  useEffect(() => {
    if (layoutId == null || layoutConfigsMap?.[layoutId] == null || selectedLayoutId === layoutId) {
      return;
    }
    updateSelectedLayoutId(layoutId);
  }, [layoutId, layoutConfigsMap, selectedLayoutId, updateSelectedLayoutId]);

  if (spaceRedirect != null) {
    return <Navigate to={spaceRedirect} replace />;
  }

  return (
    <>
      <TopScrollGradient />
      <MobileAppHeader showNavMenu={showMobileNavMenu} />
      <Routes>
        <Route index element={<TradingLayoutIndex layout={layout} tradingNavItems={tradingNavItems} />} />
        {buildOrderDetailsRoutes({
          parentRoute: 'order',
          detailsElement: <MobileOrderDetailsRoute />,
        })}
        {buildOrderDetailsRoutes({
          parentRoute: 'customer-order',
          detailsElement: <MobileOrderDetailsRoute />,
        })}
        <Route path=":panelId/:tabId" element={<MobileTradingPanelOutlet />} />
        <Route path=":panelId" element={<MobileTradingPanelOutlet />} />
        <Route path="symbol/:symbol/*" element={<MobileSymbolDeepDiveRoute navGroupName={navGroupName} />} />
      </Routes>
      <BottomScrollGradient />
      <MobileNavigationTabBar navItems={tradingNavItems} layoutSectionLabel={TOP_LEVEL_NAV_LABEL} />
    </>
  );
}

/**
 * Rendered at the layout root (`/trading/:layoutId`). Redirects to the default child destination (first
 * market sub-panel or active panel) when one exists; otherwise shows the top-level placeholder.
 */
function TradingLayoutIndex({
  layout,
  tradingNavItems,
}: {
  layout: LayoutSpec | undefined;
  tradingNavItems: NavigationItem[];
}) {
  const location = useLocation();
  const { enablePredictionMarkets } = useFeatureFlag();
  const activePanelTarget =
    layout != null ? getMobileFriendlyPanelForActiveLayoutPanel(layout, enablePredictionMarkets) : undefined;
  if (layout != null && activePanelTarget != null) {
    return <Navigate to={getTradingPanelRoute(layout, activePanelTarget.id)} replace />;
  }

  const childRedirectPath = getRedirectToDefaultChildPath(tradingNavItems, location.pathname);
  if (childRedirectPath != null) {
    return <Navigate to={childRedirectPath} replace />;
  }
  if (layout != null) {
    return <TradingPlaceholder label={NO_MOBILE_FRIENDLY_PANELS_TITLE} message={NO_MOBILE_FRIENDLY_PANELS_MESSAGE} />;
  }
  return <TradingPlaceholder label={TOP_LEVEL_NAV_LABEL} />;
}

/**
 * Resolves `:panelId` against the current layout and hands the panel to {@link MobileTradingPanelDispatcher},
 * which renders the correct mobile component for the panel's type. Falls back to the top-level placeholder
 * when the panel or layout cannot be resolved.
 */
function MobileTradingPanelOutlet() {
  const { panelId } = useParams<{ panelId: string }>();
  const layout = useAppLayoutForRouteParams(LAYOUT_SECTION);
  const panel = panelId != null ? layout?.dockViewLayout.panels[panelId] : undefined;

  if (layout == null || panel == null) {
    return <TradingPlaceholder label={TOP_LEVEL_NAV_LABEL} />;
  }

  return (
    <MobileLayoutPanelProvider>
      <MobileTradingPanelDispatcher layout={layout} panel={panel} />
    </MobileLayoutPanelProvider>
  );
}

export const TOP_LEVEL_NAV_LABEL = 'Trading';
